import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Checkbox, FormControlLabel, Radio, RadioGroup } from '@mui/material'
import { CheckBox } from '@mui/icons-material'
import { ReactComponent as CheckboxDisabledIcon } from 'ui/images/icon-checkbox-disabled.svg'
import { ReactComponent as RadioButtonDisabledIcon } from 'ui/images/icon-radio-button-disabled.svg'
import type {
  BankTransfer,
  MedicalInstitutionProps,
  SettlementSettingProps,
} from 'types/med'
import type { SettlementNameProps } from 'types/common'
import { Caption } from 'ui/shorthands/Texts'
import type { Theme } from '@mui/material/styles'
import { makeStyles } from 'tss-react/mui'
import Tooltip from '@mui/material/Tooltip'

const useStyles = makeStyles()((theme: Theme) => ({
  root: {
    marginTop: theme.spacing(-1),
    marginBottom: theme.spacing(-1),
  },
  caption: {
    padding: theme.spacing(2, 0),
  },
  icon: {
    padding: theme.spacing(1.125, 0.615, 1.125, 1), // NOTE: 非MUI iconのための微調整
    display: 'flex',
  },
  radioGroup: {
    display: 'flex',
    flexDirection: 'row',
    padding: theme.spacing(2, 0, 0, 0),
  },
  radioItem: {
    paddingRight: theme.spacing(3),
  },
}))

const creditMethods: SettlementNameProps[] = ['credit_card', 'link']

export type SettlementSettingsCheckBoxesProps = {
  med: MedicalInstitutionProps
  bank?: BankTransfer
  value: SettlementSettingProps[]
  onChange: (data: SettlementSettingProps[]) => void
}

type ItemProps = SettlementSettingProps & { disabled?: boolean }

const SettlementSettingsCheckBoxes: React.FC<
  SettlementSettingsCheckBoxesProps
> = ({ med, bank, value, onChange }: SettlementSettingsCheckBoxesProps) => {
  const { t } = useTranslation()
  const { classes } = useStyles()

  const [hasNoActiveMethods, setHasNoActiveMethods] = useState<boolean>(false)
  const activeCredit = value
    .filter((val) => creditMethods.includes(val.name))
    .find((v) => v.is_active)
  const [creditChecked, setCreditChecked] = useState<boolean>(!!activeCredit)
  const [creditMethod, setCreditMethod] = useState<
    SettlementNameProps | undefined
  >(activeCredit?.name)
  const isConnectedStripe: boolean = !!med.is_connected_stripe

  const setDisabled: (val: SettlementSettingProps) => ItemProps = (val) => {
    if (
      (val.name === 'bank_transfer' && !bank) ||
      (val.name === 'credit_card' && !isConnectedStripe)
    ) {
      return { ...val, disabled: true, is_active: false }
    }
    return val
  }

  // NOTE: 銀行、Stripeの設定がなければ強制的にinactiveにする
  const setIsActive: (name: SettlementNameProps, value: boolean) => boolean = (
    name,
    value
  ) => {
    if (
      (name === 'bank_transfer' && !bank) ||
      (name === 'credit_card' && !isConnectedStripe)
    ) {
      return false
    }
    return value
  }

  const creditValue = value
    .filter((val) => creditMethods.includes(val.name))
    .map((val) => {
      return { ...val, is_active: setIsActive(val.name, val.is_active) }
    })

  const notCreditValue = value
    .filter((val) => !creditMethods.includes(val.name))
    .map((val) => {
      return { ...val, is_active: setIsActive(val.name, val.is_active) }
    })

  const handleChange = (name: SettlementNameProps, is_active: boolean) => {
    const index = value.findIndex((v) => v.name === name)
    const newArr: SettlementSettingProps[] = [...value]
    newArr[index] = { ...value[index], is_active }
    onChange(newArr)
  }

  const switchCredit = (name?: SettlementNameProps) => {
    const creditIndexes = creditMethods
      .map((name) => value.findIndex((v) => v.name === name))
      .filter((index) => index > -1)
    const newArr: SettlementSettingProps[] = [...value]
    creditIndexes.forEach((index) => {
      const newValue: SettlementSettingProps = { ...value[index] }
      if (newValue.name === name) {
        newArr[index] = { ...newValue, is_active: true }
      } else {
        newArr[index] = { ...newValue, is_active: false }
      }
    })
    onChange(newArr)
  }

  useEffect(() => {
    if (!creditChecked) {
      switchCredit(undefined)
      return
    }
    switchCredit(creditMethod)
    if (!creditMethod) {
      // NOTE: クレジット選択した時のデフォルト
      setCreditMethod('credit_card')
      return
    }
    if (!isConnectedStripe) {
      setCreditMethod('link')
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [creditMethod, creditChecked, isConnectedStripe])

  useEffect(() => {
    setHasNoActiveMethods(!value.find((v) => v.is_active))
  }, [value])

  return (
    <div className={classes.root}>
      <FormControlLabel
        control={
          <Checkbox
            checkedIcon={<CheckBox color="primary" />}
            checked={creditChecked}
            onChange={(event) => {
              setCreditChecked((value) => !value)
              event.stopPropagation()
            }}
            color="primary"
          />
        }
        label={t('クレジットカード')}
      />
      {notCreditValue.map((originVal) => {
        const val: ItemProps = setDisabled(originVal)
        return (
          <FormControlLabel
            key={`not-credit-${val.name}`}
            control={
              val.disabled ? (
                <Tooltip
                  title={
                    // NOTE: 銀行以外でdisabledがある場合には個別対応に変更する
                    t(
                      '受付窓口 > 一般 > 決済方法で振込先の設定をしてからご利用ください。'
                    )
                  }
                  arrow
                >
                  <span className={classes.icon}>
                    <CheckboxDisabledIcon />
                  </span>
                </Tooltip>
              ) : (
                <Checkbox
                  checkedIcon={<CheckBox color="primary" />}
                  checked={val.is_active}
                  onChange={() => handleChange(val.name, !val.is_active)}
                  color="primary"
                />
              )
            }
            label={t(val.label)}
          />
        )
      })}
      {hasNoActiveMethods && (
        <div>
          <Caption className={classes.caption}>{t('決済はありません')}</Caption>
        </div>
      )}
      {creditChecked && creditMethod && (
        <RadioGroup className={classes.radioGroup} value={creditMethod}>
          {creditValue.map((originVal) => {
            const val: ItemProps = setDisabled(originVal)
            return (
              <FormControlLabel
                onChange={() => setCreditMethod(val.name)}
                key={`credit-${val.name}`}
                value={val.name}
                control={
                  val.name === 'credit_card' && !isConnectedStripe ? (
                    <Tooltip
                      title={t(
                        '受付窓口 > 一般 > 決済方法でStripe決済の設定をしてからご利用ください。'
                      )}
                      arrow
                    >
                      <span className={classes.icon}>
                        <RadioButtonDisabledIcon />
                      </span>
                    </Tooltip>
                  ) : (
                    <Radio color={'primary'} />
                  )
                }
                label={t(val.label)}
                className={classes.radioItem}
              />
            )
          })}
        </RadioGroup>
      )}
    </div>
  )
}

export default SettlementSettingsCheckBoxes
